// URL validation for untrusted values before they reach a navigation sink.
// Duplicated near-verbatim across the Reggora frontends; keep names and semantics
// identical.
//
// Parsing is left to `new URL()`: the parser strips control characters and
// interior tabs/newlines the way a browser does, so `java<TAB>script:` cannot
// slip past. Same-origin checks compare resolved origins, which is what catches
// `//host`, `/\host` and `\\host`. Protocol is checked explicitly rather than
// inferred from `origin === "null"`, since `blob:` inherits the origin it wraps.

const RENDERABLE_PROTOCOLS = ['http:', 'https:']
// Navigation is stricter than rendering: an http destination is a downgrade an
// attacker can force, so only https may be navigated to or opened in a tab.
const NAVIGABLE_PROTOCOLS = ['https:']

const parseAbsolute = (value, protocols) => {
  // Type-guarded because URL stringifies its input: ['https://x'] would pass.
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  let url
  try {
    url = new URL(value) // no base, so relative and protocol-relative throw
  } catch (err) {
    return null
  }

  if (!protocols.includes(url.protocol)) {
    return null
  }

  // Userinfo before an `@` is not the host, so it can spoof one.
  if (url.username || url.password) {
    return null
  }

  return url
}

// Without `allowedOrigins` this is only a scheme gate — it says "will not run
// script", not "goes somewhere we trust". Callers appending a credential to the
// URL must pass one.
export const isSafeExternalUrl = (value, allowedOrigins = null) => {
  const url = parseAbsolute(value, NAVIGABLE_PROTOCOLS)
  if (!url) {
    return false
  }

  return allowedOrigins ? allowedOrigins.includes(url.origin) : true
}

const internalPath = (value) => {
  if (typeof value !== 'string' || value[0] !== '/') {
    return null
  }

  try {
    const { origin } = window.location
    const url = new URL(value, origin)
    if (url.origin !== origin) {
      return null
    }
    return url.pathname + url.search + url.hash
  } catch (err) {
    return null
  }
}

export const isSafeInternalPath = (value) => internalPath(value) !== null

// Returns undefined by default so React omits the href entirely, rendering a
// rejected URL as a non-navigating link.
export const safeHref = (value, fallback = undefined) => {
  const path = internalPath(value)
  if (path !== null) {
    return path
  }

  const url = parseAbsolute(value, RENDERABLE_PROTOCOLS)
  return url ? url.href : fallback
}

// Passes `url.href` rather than `value`: handing the sink the raw string would
// give it a value the check never saw. Returns nothing, because `noopener` nulls
// the handle even on success and so cannot signal whether the open worked.
export const openExternal = (value) => {
  const url = parseAbsolute(value, NAVIGABLE_PROTOCOLS)
  if (!url) {
    return
  }

  // eslint-disable-next-line no-restricted-properties -- this IS the validator; parseAbsolute above vetted the URL
  window.open(url.href, '_blank', 'noopener,noreferrer')
}
